import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, CheckCircle2, Loader2 } from "lucide-react";
import api, { FILE_BASE } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../lib/cart";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let alive = true;

    const poll = async (n = 0) => {
      if (n >= 8) {
        if (alive) setStatus("timeout");
        return;
      }
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (!alive) return;
        setData(r.data);
        setAttempts(n + 1);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (err) {
        if (alive) setStatus("error");
      }
    };
    poll();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="App">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {status === "polling" && (
          <div className="card-surface p-12 text-center" data-testid="checkout-polling">
            <Loader2 className="w-12 h-12 mx-auto text-brand animate-spin mb-4" />
            <h1 className="font-display font-black uppercase text-white text-3xl mb-2">Bekräftar betalning</h1>
            <p className="text-white/60">Försök {attempts}/8 — vänta...</p>
          </div>
        )}

        {status === "paid" && data && (
          <div data-testid="checkout-success">
            <div className="text-center mb-10">
              <CheckCircle2 className="w-16 h-16 mx-auto text-brand mb-4" />
              <p className="label-overline mb-2">Tack!</p>
              <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-2">Betalningen lyckades</h1>
              <p className="text-white/70">
                {data.amount_total ? `${(data.amount_total / 100).toFixed(0)} ${data.currency?.toUpperCase()}` : ""} •{" "}
                {data.downloads.length} foton redo att laddas ner
              </p>
            </div>

            <h2 className="font-display font-bold uppercase text-white text-xl mb-4">Dina nedladdningar</h2>
            <div className="space-y-2" data-testid="downloads-list">
              {data.downloads.map((d, i) => (
                <a
                  key={d.photo_id}
                  href={`${FILE_BASE}${d.url}`}
                  download
                  className="card-surface flex items-center gap-4 p-3 hover:border-brand/50"
                  data-testid={`download-${i}`}
                >
                  <img src={`${FILE_BASE}/api/files/thumb/${d.photo_id}`} alt="" className="w-20 h-16 object-cover" />
                  <div className="flex-1">
                    <p className="text-white text-sm">Original-foto · utan vattenstämpel</p>
                    <p className="text-xs text-white/50 font-mono">{d.photo_id.slice(0, 8)}...</p>
                  </div>
                  <span className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
                    <Download className="w-4 h-4" /> Ladda ner
                  </span>
                </a>
              ))}
            </div>

            <p className="text-xs text-white/40 mt-6 text-center">
              Länkarna är personliga och giltiga obegränsat. Vi har även mailat dem till dig.
            </p>
            <div className="text-center mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={`/receipt/${data.order_id}?email=${encodeURIComponent(data.customer_email || "")}`}
                className="btn-secondary inline-flex"
                data-testid="receipt-link"
              >
                Visa kvitto
              </a>
              <Link to="/search" className="btn-secondary inline-flex">Tillbaka till sök</Link>
            </div>
          </div>
        )}

        {(status === "expired" || status === "timeout" || status === "error") && (
          <div className="card-surface p-12 text-center" data-testid="checkout-failure">
            <h1 className="font-display font-black uppercase text-white text-3xl mb-2">Något gick fel</h1>
            <p className="text-white/60 mb-6">
              {status === "expired"
                ? "Betalningssessionen har gått ut."
                : status === "timeout"
                ? "Det tar längre tid än väntat. Kolla din mejl eller försök igen."
                : "Vi kunde inte verifiera betalningen."}
            </p>
            <Link to="/cart" className="btn-primary inline-flex">Tillbaka till varukorg</Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
